// src/features/executive/export/use-export-excel.ts
// Orchestrator for Phase 1F Excel export. Drill components pass their raw
// data payload + drillId; this hook composes metadata + triggers download.

import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { saveAs } from 'file-saver'
import type {
  RevenueTrendResponse,
  AppointmentDetailRow,
  KeyProcedureDetailRow,
  TopProceduresResponse,
  TopDoctorsResponse,
  TopProductsResponse,
  CategoryMixResponse,
  NewPatientDetailRow,
  ExecutiveFilterState,
} from 'api/executive/executive.types'
import { getClinicInfo } from 'utils/app.utils'
import { buildWorkbook } from './excel-builder'
import {
  revenueExcelPayload,
  keyProceduresExcelPayload,
  appointmentsExcelPayload,
  topProceduresExcelPayload,
  topDoctorsExcelPayload,
  topProductsExcelPayload,
  categoryMixExcelPayload,
  newPatientTrendExcelPayload,
} from './excel-columns'
import type { ExcelExportPayload } from './excel-columns'
import { buildExcelFilename, formatReadableTimestamp } from './format'
import { swalSuccess, swalError } from 'component/Alert/Swal'

export type DrillExportData =
  | { drillId: 'revenue'; data: RevenueTrendResponse | null }
  | { drillId: 'key-procedures'; data: KeyProcedureDetailRow[] }
  | { drillId: 'appointments'; data: AppointmentDetailRow[] }
  | { drillId: 'top-procedures'; data: TopProceduresResponse | null }
  | { drillId: 'top-doctors'; data: TopDoctorsResponse | null }
  | { drillId: 'top-products'; data: TopProductsResponse | null }
  | { drillId: 'category-mix'; data: CategoryMixResponse | null }
  | { drillId: 'new-patient-trend'; data: NewPatientDetailRow[] }

interface ExecRoot {
  executive: { filters: ExecutiveFilterState }
}

export function useExportExcel() {
  const { t } = useTranslation()
  const filters = useSelector((s: ExecRoot) => s.executive.filters)
  const [error, setError] = useState<Error | null>(null)

  const exportDrill = useCallback(
    async (payload: DrillExportData, widgetTitle: string) => {
      setError(null)
      try {
        const now = new Date()
        const generatedAt = formatReadableTimestamp(now)
        const branchInfos = getClinicInfo()
        const clinicDisplayName = branchInfos?.clinicName || branchInfos?.clinicNameEn || 'Cliniter Dental'

        // Compose filter summary from active filters.
        const chips: string[] = []
        const { dateRange, branchId, ...scalars } = filters
        chips.push(`ช่วงเวลา=${dateRange.preset}`)
        if (branchId != null) chips.push(`สาขา=${branchId}`)
        Object.entries(scalars).forEach(([k, v]) => {
          if (v != null) chips.push(`${k}=${v}`)
        })
        const filterSummary = chips.join(' · ')

        // Build payload per drillId — switchboard on typed union.
        let excelPayload: ExcelExportPayload<any>
        switch (payload.drillId) {
          case 'revenue':
            excelPayload = revenueExcelPayload(payload.data, {
              date: t('EXECUTIVE.DRILL.DATE'),
              revenue: t('EXECUTIVE.DRILL.REVENUE'),
              share: t('EXECUTIVE.DRILL.SHARE'),
            })
            break
          case 'key-procedures':
            excelPayload = keyProceduresExcelPayload(payload.data, {
              procedure: t('EXECUTIVE.SALES.COLUMN.NAME'),
              date: t('EXECUTIVE.DRILL.DATE'),
              cases: t('EXECUTIVE.SALES.COLUMN.CASES'),
              revenue: t('EXECUTIVE.SALES.COLUMN.REVENUE'),
            })
            break
          case 'appointments':
            excelPayload = appointmentsExcelPayload(payload.data, {
              date: t('EXECUTIVE.DRILL.DATE'),
              total: t('EXECUTIVE.PERFORMANCE.APPOINTMENTS.TOTAL', 'รวม'),
              completed: t('EXECUTIVE.PERFORMANCE.APPOINTMENTS.COMPLETED', 'เสร็จสิ้น'),
              cancelled: t('EXECUTIVE.PERFORMANCE.APPOINTMENTS.CANCELLED', 'ยกเลิก'),
              noShow: t('EXECUTIVE.PERFORMANCE.APPOINTMENTS.NO_SHOW', 'ไม่มา'),
            })
            break
          case 'top-procedures':
            excelPayload = topProceduresExcelPayload(payload.data, {
              rank: t('EXECUTIVE.SALES.COLUMN.RANK'),
              name: t('EXECUTIVE.SALES.COLUMN.NAME'),
              cases: t('EXECUTIVE.SALES.COLUMN.CASES'),
              revenue: t('EXECUTIVE.SALES.COLUMN.REVENUE'),
              avg: t('EXECUTIVE.SALES.COLUMN.AVG_PER_CASE'),
              share: t('EXECUTIVE.DRILL.SHARE'),
            })
            break
          case 'top-doctors':
            excelPayload = topDoctorsExcelPayload(payload.data, {
              rank: t('EXECUTIVE.SALES.COLUMN.RANK'),
              name: t('EXECUTIVE.SALES.COLUMN.NAME'),
              cases: t('EXECUTIVE.SALES.COLUMN.CASES'),
              revenue: t('EXECUTIVE.SALES.COLUMN.REVENUE'),
              avg: t('EXECUTIVE.SALES.COLUMN.AVG_PER_CASE'),
              share: t('EXECUTIVE.DRILL.SHARE'),
            })
            break
          case 'top-products':
            excelPayload = topProductsExcelPayload(payload.data, {
              rank: t('EXECUTIVE.SALES.COLUMN.RANK'),
              name: t('EXECUTIVE.SALES.COLUMN.NAME'),
              units: t('EXECUTIVE.SALES.COLUMN.UNITS'),
              revenue: t('EXECUTIVE.SALES.COLUMN.REVENUE'),
              avg: t('EXECUTIVE.SALES.COLUMN.AVG_PER_CASE', 'เฉลี่ย/หน่วย'),
              share: t('EXECUTIVE.DRILL.SHARE'),
            })
            break
          case 'category-mix':
            excelPayload = categoryMixExcelPayload(payload.data, {
              name: t('EXECUTIVE.SALES.COLUMN.NAME'),
              revenue: t('EXECUTIVE.SALES.COLUMN.REVENUE'),
              share: t('EXECUTIVE.DRILL.SHARE'),
            })
            break
          case 'new-patient-trend':
            excelPayload = newPatientTrendExcelPayload(payload.data, {
              date: t('EXECUTIVE.DRILL.DATE'),
              count: t('EXECUTIVE.INSIGHT.NEW_TREND.COUNT', 'New patients'),
              sampleNames: t('EXECUTIVE.INSIGHT.NEW_TREND.SAMPLE_NAMES', 'Sample names'),
            })
            break
          default: {
            const _exhaustive: never = payload
            throw new Error(`Unknown drillId: ${JSON.stringify(_exhaustive)}`)
          }
        }

        const blob = await buildWorkbook({
          sheetTitle: widgetTitle,
          headerTitle: t('EXECUTIVE.EXPORT.EXCEL_SHEET_TITLE', { clinicName: clinicDisplayName }),
          widgetTitle,
          generatedAtLabel: t('EXECUTIVE.EXPORT.EXCEL_GENERATED_AT'),
          generatedAt,
          filtersLabel: t('EXECUTIVE.EXPORT.EXCEL_FILTERS'),
          filterSummary: filterSummary || '—',
          creator: `${clinicDisplayName} Executive Dashboard`,
          columns: excelPayload.columns,
          rows: excelPayload.rows,
        })

        saveAs(blob, buildExcelFilename(payload.drillId, clinicDisplayName, now))
        swalSuccess(t('EXECUTIVE.EXPORT.SUCCESS'))
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e))
        setError(err)
        console.error('[Phase 1F] Excel export failed', err)
        swalError(t('EXECUTIVE.EXPORT.ERROR'))
      }
    },
    [filters, t]
  )

  return { exportDrill, error }
}
